import React from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { gql } from '@apollo/client';
import Breadcrumb from '../components/Breadcrumb';
import QueryError from '../components/QueryError';
import M3Card from '../components/M3Card';
import M3Chip from '../components/M3Chip';
import M3LinearProgress from '../components/M3Progress';
import { UPDATE_FIGURE, DELETE_FIGURE } from '../graphql/mutations';
import { LITHICS_QUERY } from '../graphql/queries';

const FIGURE_QUERY = gql`
  query Figure($id: ID!) {
    figure(id: $id) {
      id
      identifier
      type
      x1
      y1
      x2
      y2
      area
      perimeter
      angle
      probability
      verified
      disturbed
      siteId
      publicationId
      pageId
      publication {
        id
        title
        author
      }
      page {
        number
      }
      site {
        id
        name
      }
      tags {
        id
        name
      }
      createdAt
    }
  }
`;

export default function FigureShowPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, loading, error } = useQuery(FIGURE_QUERY, { variables: { id } });

  const [deleteFigure] = useMutation(DELETE_FIGURE, {
    refetchQueries: [{ query: LITHICS_QUERY }],
    async onCompleted() {
      navigate('/lithics');
    }
  });

  if (error)
    return (
      <div className="page-content">
        <QueryError error={error} onRetry={() => window.location.reload()} />
      </div>
    );

  if (loading)
    return (
      <div className="page-content">
        <M3LinearProgress indeterminate />
      </div>
    );

  const figure = data?.figure;
  if (!figure) return <div className="page-content">Figure not found.</div>;

  return (
    <div className="page-content detail-page">
      <Breadcrumb
        items={[
          { label: 'Lithics', link: '/lithics' },
          { label: figure.identifier || `Figure #${figure.id}` }
        ]}
      />

      <div className="detail-header">
        <div>
          <h1>{figure.identifier || `Figure #${figure.id}`}</h1>
          <M3Chip variant="assist">{figure.type}</M3Chip>
        </div>
        <div className="detail-actions">
          <Link to={`/figures/lithics/${id}/edit`} className="btn btn-secondary">
            <span className="material-symbols-rounded md-18">edit</span>
            Edit
          </Link>
          <button
            className="btn btn-text text-error"
            onClick={() => {
              if (confirm('Delete this figure?')) deleteFigure({ variables: { input: { id } } });
            }}
          >
            <span className="material-symbols-rounded md-18">delete</span>
            Delete
          </button>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-md-6">
          <M3Card variant="outlined">
            <M3Card.Header title="Measurements" />
            <M3Card.Body>
              <div className="detail-row">
                <span className="detail-label">Bounding Box</span>
                <span className="detail-value">
                  [{figure.x1}, {figure.y1}, {figure.x2}, {figure.y2}]
                </span>
              </div>
              {figure.area != null && (
                <div className="detail-row">
                  <span className="detail-label">Area</span>
                  <span className="detail-value">{figure.area.toFixed(2)}</span>
                </div>
              )}
              {figure.perimeter != null && (
                <div className="detail-row">
                  <span className="detail-label">Perimeter</span>
                  <span className="detail-value">{figure.perimeter.toFixed(2)}</span>
                </div>
              )}
              {figure.angle != null && (
                <div className="detail-row">
                  <span className="detail-label">Angle</span>
                  <span className="detail-value">{figure.angle.toFixed(1)}°</span>
                </div>
              )}
              {figure.probability != null && (
                <div className="detail-row">
                  <span className="detail-label">Probability</span>
                  <span className="detail-value">{figure.probability.toFixed(3)}</span>
                </div>
              )}
            </M3Card.Body>
          </M3Card>
        </div>

        <div className="col-md-6">
          <M3Card variant="outlined">
            <M3Card.Header title="Status" />
            <M3Card.Body>
              <div className="detail-row">
                <span className="detail-label">Verified</span>
                <span className="detail-value">{figure.verified ? '✓' : '—'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Disturbed</span>
                <span className="detail-value">{figure.disturbed ? 'Yes' : 'No'}</span>
              </div>
            </M3Card.Body>
          </M3Card>
        </div>

        <div className="col-md-6">
          <M3Card variant="outlined">
            <M3Card.Header title="References" />
            <M3Card.Body>
              {figure.publication && (
                <div className="detail-row">
                  <span className="detail-label">Publication</span>
                  <span className="detail-value">
                    <Link to={`/publications/${figure.publication.id}`}>
                      {figure.publication.title}
                    </Link>
                  </span>
                </div>
              )}
              {figure.page && (
                <div className="detail-row">
                  <span className="detail-label">Page</span>
                  <span className="detail-value">{figure.page.number + 1}</span>
                </div>
              )}
              {figure.site && (
                <div className="detail-row">
                  <span className="detail-label">Site</span>
                  <span className="detail-value">
                    <Link to={`/sites/${figure.site.id}`}>{figure.site.name}</Link>
                  </span>
                </div>
              )}
            </M3Card.Body>
          </M3Card>
        </div>

        {figure.tags?.length > 0 && (
          <div className="col-md-6">
            <M3Card variant="outlined">
              <M3Card.Header title="Tags" />
              <M3Card.Body>
                <div className="d-flex gap-2 flex-wrap">
                  {figure.tags.map((tag) => (
                    <M3Chip key={tag.id} variant="input">
                      {tag.name}
                    </M3Chip>
                  ))}
                </div>
              </M3Card.Body>
            </M3Card>
          </div>
        )}
      </div>
    </div>
  );
}
